import React, { useState, useRef, useEffect } from "react";
import "./EditTodoList.scss";
import "../../../../components/ModalContent/ModalComponents.scss";
import { normalizeColor } from "../../../../utils/colors";
import ColorPicker from "../../../../components/ColorPicker/ColorPicker";
import { useTranslation } from "react-i18next";

function EditTodoList({ todoListData, updateTodoList, cancelEditTodoList }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(todoListData?.title || "");
  const [selectedColor, setSelectedColor] = useState(
    normalizeColor(todoListData?.color),
  );
  const [listType, setListType] = useState(todoListData?.type || "todo");
  const [showTitleError, setShowTitleError] = useState(false);

  // Update state when todoListData changes
  useEffect(() => {
    if (todoListData) {
      setTitle(todoListData.title || "");
      setSelectedColor(normalizeColor(todoListData.color));
      setListType(todoListData.type || "todo");
    }
  }, [todoListData]);

  const handleUpdateTodoList = () => {
    if (title.trim()) {
      const colorHex = normalizeColor(selectedColor);
      updateTodoList(todoListData._id, title.trim(), colorHex, listType);
      setShowTitleError(false);
    } else {
      setShowTitleError(true);
    }
  };

  // Set focus on input field when modal opens
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select(); // Select all text for easy editing
      }
    }, 100); // 100ms delay to ensure DOM is ready
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="edit-todolist-content">
      <div className="modal-title">
        <div>{t("lists.editList")}</div>
      </div>
      <div className="modal-form">
        <div className="modal-form-title">{t("common.title")}</div>
        <input
          ref={inputRef}
          type="text"
          className="modal-text-input"
          placeholder={t("lists.modals.titlePlaceholder")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (showTitleError && e.target.value.trim()) {
              setShowTitleError(false);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleUpdateTodoList();
            }
          }}
        />
        {showTitleError && (
          <div className="modal-helper-text">
            {t("lists.modals.titleRequired")}
          </div>
        )}

        <div className="modal-form-title">{t("lists.type")}</div>
        <div className="modal-checkbox-group">
          <label className="modal-checkbox-label">
            <input
              type="checkbox"
              checked={listType === "todo"}
              onChange={() => setListType("todo")}
            />
            <span>{t("lists.todoList")}</span>
          </label>
          <label className="modal-checkbox-label">
            <input
              type="checkbox"
              checked={listType === "checklist"}
              onChange={() => setListType("checklist")}
            />
            <span>{t("lists.checklist")}</span>
          </label>
        </div>

        <div className="modal-form-title">{t("common.color")}</div>
        <div className="color-picker-wrapper">
          <ColorPicker
            selectedColor={selectedColor}
            onColorSelect={setSelectedColor}
          />
        </div>
        <div className="modal-form-buttons">
          <button
            className="modal-button cancel"
            onClick={() => {
              cancelEditTodoList();
            }}
          >
            {t("common.cancel")}
          </button>
          <button className="modal-button save" onClick={handleUpdateTodoList}>
            {t("common.update")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditTodoList;
